import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { getOrgActivity } from "api/onboardingApi";
import { unwrapList, unwrapObject } from "api/unwrap";

/**
 * /compliance/activity — org-wide newest-first event feed across every driver.
 * Filterable by event type + search across driver name / event text.
 */

const TYPE_META = {
  ONBOARDING_STARTED:  { color: "#3b82f6", group: "Onboarding" },
  READY_TO_DRIVE:      { color: "#16a34a", group: "Onboarding" },
  REJECTED:            { color: "#dc2626", group: "Onboarding" },
  TRAINING_ASSIGNED:   { color: "#0ea5e9", group: "Training" },
  TRAINING_PASSED:     { color: "#16a34a", group: "Training" },
  TRAINING_WAIVED:     { color: "#a855f7", group: "Training" },
  TRAINING_EXPIRED:    { color: "#dc2626", group: "Training" },
  POLICY_SIGNED:       { color: "#16a34a", group: "Policy" },
  POLICY_WAIVED:       { color: "#a855f7", group: "Policy" },
  POLICY_EXPIRED:      { color: "#dc2626", group: "Policy" },
  DOCUMENT_UPLOADED:   { color: "#0ea5e9", group: "Document" },
  DOCUMENT_VERIFIED:   { color: "#16a34a", group: "Document" },
};
const GROUPS = ["all", "Onboarding", "Training", "Policy", "Document"];

const DAY_MS = 24 * 60 * 60 * 1000;

function bucketFor(ms) {
  const days = (Date.now() - ms) / DAY_MS;
  if (days < 1) return "today";
  if (days < 7) return "thisWeek";
  if (days < 30) return "thisMonth";
  return "older";
}
const BUCKET_LABEL = {
  today: "Today",
  thisWeek: "This week",
  thisMonth: "This month",
  older: "Older",
};

export const OrgActivityPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("all");
  const [limit, setLimit] = useState(200);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getOrgActivity({ limit });
      setEvents(unwrapList(res));
    } catch (e) {
      setError(e?.message || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((e) => {
      if (group !== "all") {
        const g = TYPE_META[e.type]?.group || "Other";
        if (g !== group) return false;
      }
      if (!q) return true;
      const hay = `${e.driverFullName || ""} ${e.summary || ""} ${e.detail || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [events, search, group]);

  const groupedByBucket = useMemo(() => {
    const g = { today: [], thisWeek: [], thisMonth: [], older: [] };
    filtered.forEach((e) => {
      const b = bucketFor(e.timestamp || 0);
      g[b].push(e);
    });
    return g;
  }, [filtered]);

  return (
    <Stack spacing={2} sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f8fafc", minHeight: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Org Activity ({events.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Every onboarding milestone, training, policy, and document event across every driver — newest first.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            select
            SelectProps={{ native: true }}
            size="small"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            label="Limit"
            sx={{ width: 110 }}
            InputLabelProps={{ shrink: true }}
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </TextField>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search driver name / event text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 240 }}
        />
        {GROUPS.map((g) => (
          <Chip
            key={g}
            size="small"
            label={g === "all" ? "All" : g}
            onClick={() => setGroup(g)}
            sx={{
              backgroundColor: group === g ? "#1F4E78" : "#f1f5f9",
              color: group === g ? "#ffffff" : "#475569",
              fontWeight: group === g ? 700 : 500,
              cursor: "pointer",
            }}
          />
        ))}
      </Box>

      {loading && events.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              {events.length === 0 ? "No activity yet." : "No activity matches the current filter."}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedByBucket).map(([key, list]) =>
          list.length > 0 ? (
            <Card key={key} variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {BUCKET_LABEL[key]} ({list.length})
                </Typography>
                <Stack divider={<Divider />} spacing={0}>
                  {list.map((e, i) => {
                    const meta = TYPE_META[e.type] || { color: "#475569", group: "Other" };
                    return (
                      <Box
                        key={`${e.driverDetailsId}-${e.type}-${e.timestamp}-${i}`}
                        sx={{ display: "flex", gap: 2, py: 1.25, alignItems: "center" }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: meta.color,
                            flexShrink: 0,
                            mt: 0.5,
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                            {e.summary}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            <strong>{e.driverFullName}</strong>
                            {e.detail ? ` · ${e.detail}` : ""}
                          </Typography>
                        </Box>
                        <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(e.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          endIcon={<OpenInNewIcon fontSize="small" />}
                          onClick={() => navigate(`/compliance/drivers/${e.driverDetailsId}`)}
                        >
                          Open
                        </Button>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          ) : null
        )
      )}
    </Stack>
  );
};

export default OrgActivityPage;
